import { useState, useEffect, useRef } from 'react';

interface DeleteModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void | Promise<void>;
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  loading?: boolean;
}

export function DeleteModal({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  confirmText = 'Yes, delete',
  cancelText = 'No, cancel',
  loading = false,
}: DeleteModalProps) {
  const [isVisible, setIsVisible] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const modalRef = useRef<HTMLDivElement>(null);

  // Handle open/close animations
  useEffect(() => {
    if (isOpen) {
      setIsVisible(true);
      // Double RAF ensures browser has painted display:flex before transitioning opacity
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setIsAnimating(true);
        });
      });
    } else if (isVisible) {
      setIsAnimating(false);
      const timer = setTimeout(() => {
        setIsVisible(false);
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [isOpen, isVisible]);

  // Handle escape key
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !loading) {
        onClose();
      }
    };

    if (isVisible) {
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isVisible, onClose, loading]);

  const handleConfirm = async () => {
    await onConfirm();
  };

  if (!isVisible) return null;

  return (
      <div 
        className={`delete-modal ${isVisible ? 'is-visible' : ''} ${isAnimating ? 'is-open' : ''} ${!isAnimating && isVisible ? 'is-closing' : ''}`}
      >
      <div className="white-background" ref={modalRef}>
        <div 
          className="modal-close"
          onClick={loading ? undefined : onClose}
          style={{ cursor: loading ? 'not-allowed' : 'pointer', opacity: loading ? 0.5 : 1 }}
        >
          <div className="rotated-text">+</div>
        </div>
        
        <div className="text-size-medium text-weight-bold">{title}</div>
        <div className="spacer-xtiny"></div>
        <div className="text-size-regular text-colour-alt-grey">{message}</div>
        <div className="spacer-small"></div>
        
        <div className="button-container">
          <a 
            href="#" 
            className="button is-red w-button"
            onClick={(e) => { e.preventDefault(); handleConfirm(); }}
            style={{ opacity: loading ? 0.6 : 1, pointerEvents: loading ? 'none' : 'auto' }}
          >
            {loading ? 'Please wait...' : confirmText}
          </a>
          <div className="vertical-spacer"></div>
          <a 
            href="#" 
            className="button is-white has-inner-shadow w-button"
            onClick={(e) => { e.preventDefault(); onClose(); }}
            style={{ opacity: loading ? 0.6 : 1, pointerEvents: loading ? 'none' : 'auto' }}
          >
            {cancelText}
          </a>
        </div>
      </div>
      
      <div 
        className="close-layer" 
        onClick={loading ? undefined : onClose}
        style={{ cursor: loading ? 'not-allowed' : 'pointer' }}
      ></div>
    </div>
  );
}

